import { useMutation } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { type JSX, useState } from 'react'
import { useCurrentUser } from '~/hooks'
import { RouterOutput, useTRPC } from '~/services/trpc'

import { Badge, Button, Icon, Tooltip, TooltipTrigger, cn } from '@hap/components'
import { ScorecardScale, versionedScaleOptions } from '@hap/constants'
import { calculateCriterionPercentile } from '@hap/utils'

import { CriteriaTooltip } from './criteria-tooltip'

export function ScorecardCriteriaItem(props: {
  criteria: RouterOutput['evaluations']['getByContactLogId'][number]['scorecard']['sections'][number]['criteria'][number]
  score?: RouterOutput['evaluations']['getByContactLogId'][number]['scores'][number]
  evaluationId: string
  contactLogId: string
  published: boolean
  workspaceId: string | null
  userId: Nullable<string>
  autoEvaluation: Nullable<boolean>
  activeDispute?: RouterOutput['evaluationCriteriaDisputes']['listByEvaluationId'][number]
}): JSX.Element {
  const [hover, setHover] = useState(false)
  const hasActiveDispute = !!props.activeDispute && !props.activeDispute.resolvedAt

  return (
    <>
      <li
        key={props.criteria.id}
        className="group border-t pr-0 pl-4"
        onMouseOver={() => setHover(true)}
        onMouseOut={() => setHover(false)}
      >
        <Tooltip>
          <TooltipTrigger asChild disabled={!!props.score?.notes}>
            <div className="flex w-full">
              <div className="grow">
                <p
                  className={cn('grow pt-3 text-base font-semibold', {
                    'font-normal opacity-50': props.published && !props.score,
                  })}
                >
                  {props.criteria.name}{' '}
                  {hasActiveDispute && <Badge variant="warn">Disputed</Badge>}
                  {props.autoEvaluation &&
                    !props.criteria.auto &&
                    props.score?.updatedBy === null && (
                      <Badge variant="warn">Requires Manual Review</Badge>
                    )}
                </p>
              </div>
              <div
                className={cn('flex shrink-0 items-center p-1 text-2xl', {
                  'opacity-0 group-hover:opacity-100': props.published && !props.score,
                })}
              >
                <ScoreLabel
                  scoreId={props.score?.id}
                  rating={props.criteria.scale}
                  awardedPoints={props.score?.awardedPoints}
                  contactLogId={props.contactLogId}
                  evaluationId={props.evaluationId}
                  criteriaAssignmentId={props.criteria.id}
                  hover={hover}
                  autoFail={props.criteria.autoFail}
                  finalized={props.published}
                  hasActiveDispute={hasActiveDispute}
                />
              </div>
            </div>
          </TooltipTrigger>
          <CriteriaTooltip
            align="end"
            side="right"
            name={props.criteria.name}
            definition={props.criteria.definition}
            scale={props.criteria.scale}
            notes={props.score?.notes}
            auto={props.score?.auto}
            autoScoreCriteria={props.criteria.auto}
            updatedBy={props.score?.updatedBy || null}
            autoFail={props.criteria.autoFail}
            userId={props.userId}
            awardedPoints={props.score?.awardedPoints}
            criteriaAssignmentId={props.criteria.id}
            criteriaId={props.criteria.criteriaId}
            evaluationScoreId={props.score?.id}
            evaluationId={props.evaluationId}
            actions={
              props.workspaceId
                ? [
                    {
                      label: <Icon name="sliders-horizontal" />,
                      to: `/settings/workspaces/${props.workspaceId}/scorecards/${props.criteria.scorecardId}?edit=${props.criteria.id}`,
                    },
                  ]
                : []
            }
            score={props.score?.score}
          />
        </Tooltip>
      </li>
    </>
  )
}

function ScoreLabel(props: {
  scoreId?: string | null
  awardedPoints?: number | null
  autoFail: boolean
  rating: ScorecardScale
  evaluationId: string
  contactLogId: string
  criteriaAssignmentId: string
  hover: boolean
  finalized: boolean
  hasActiveDispute?: boolean
}): JSX.Element {
  const trpc = useTRPC()
  const currentUser = useCurrentUser()
  const ratingOptions = versionedScaleOptions[20250231][props.rating]
  const [hoverIndex, setHoverIndex] = useState(() => {
    if (props.awardedPoints === null || props.awardedPoints === undefined)
      return ratingOptions.length
    return ratingOptions.length - props.awardedPoints - 1
  })
  const [dirty, setDirty] = useState(false)
  const queryClient = useQueryClient()
  const mutate = useMutation(
    trpc.evaluations.setScore.mutationOptions({
      async onMutate({ awardedPoints }) {
        await queryClient.cancelQueries(trpc.evaluations.getByContactLogId.pathFilter())
        const previousEvaluations = queryClient.getQueryData(
          trpc.evaluations.getByContactLogId.queryKey(props.contactLogId),
        )
        queryClient.setQueryData(
          trpc.evaluations.getByContactLogId.queryKey(props.contactLogId),
          (prev) => {
            if (!prev) return prev
            return prev.reduce<typeof prev>((evaluations, evaluation) => {
              if (evaluation.id === props.evaluationId) {
                const totals = evaluation.scores.reduce(
                  (acc, s) => {
                    const nextScore =
                      s.scorecardCriteriaAssignmentId === props.criteriaAssignmentId
                        ? calculateCriterionPercentile({
                            scale: props.rating,
                            points: awardedPoints,
                          })
                        : s.score
                    if (nextScore === null) return acc

                    const criteria = evaluation.scorecard.sections
                      .flatMap((s) => s.criteria)
                      .find((c) => c.id === s.scorecardCriteriaAssignmentId)
                    if (!criteria) {
                      console.warn('missing criteria. this should never happen')
                      return acc
                    }
                    const weightAwardedPoints = criteria.weight * nextScore
                    const weightPossiblePoints = criteria.weight
                    return {
                      totalPoints: acc.totalPoints + weightPossiblePoints,
                      awardedPoints: acc.awardedPoints + weightAwardedPoints,
                      autoFail:
                        acc.autoFail || (criteria.autoFail && weightAwardedPoints === 0),
                    }
                  },
                  {
                    autoFail: false,
                    totalPoints: 0,
                    awardedPoints: 0,
                  },
                )
                const realScore =
                  totals.totalPoints > 0
                    ? totals.awardedPoints / totals.totalPoints
                    : null
                const score = totals.autoFail ? 0 : realScore
                evaluations.push({
                  ...evaluation,
                  totalPoints: totals.totalPoints,
                  awardedPoints: totals.awardedPoints,
                  realScore,
                  score,
                  autoFail: totals.autoFail,
                  scores: evaluation.scores.map((s) => {
                    if (s.scorecardCriteriaAssignmentId === props.criteriaAssignmentId) {
                      return {
                        ...s,
                        awardedPoints,
                        updatedBy: currentUser?.id || null,
                      }
                    }
                    return s
                  }),
                })
              } else {
                evaluations.push(evaluation)
              }
              return evaluations
            }, [])
          },
        )
        return { previousEvaluations }
      },
      onSuccess() {
        queryClient.invalidateQueries(
          trpc.evaluations.getByContactLogId.queryOptions(props.contactLogId),
        )
      },
    }),
  )
  const handleChange = (awardedPoints: number | null) => {
    if (props.hasActiveDispute) return
    setDirty(true)
    mutate.reset()
    mutate.mutate({
      awardedPoints,
      scorecardCriteriaAssignmentId: props.criteriaAssignmentId,
      evaluationId: props.evaluationId,
    })
  }

  function hoverStyle() {
    if (props.hover) return true
    if (props.awardedPoints !== null) return false
    if (dirty) return false
    if (props.finalized) return false

    return true
  }

  return (
    <div
      className="relative h-[35px] items-stretch overflow-hidden rounded-md border transition-all"
      style={{
        width: hoverStyle()
          ? `${(ratingOptions.length + 1) * TRANSLATE_OFFSET_PX + 1}px`
          : '35px',
      }}
    >
      <div
        className="bg-input-focus-border/20 absolute top-[2px] right-[2px] h-[29px] w-[29px] rounded-sm transition-transform"
        style={{
          transform: `translateX(${hoverIndex * -TRANSLATE_OFFSET_PX}px)`,
        }}
      />
      <Button
        variant="ghost"
        size="icon"
        disabled={props.hasActiveDispute}
        onMouseOver={() => setHoverIndex(ratingOptions.length)}
        onClick={() => handleChange(null)}
        style={{
          transform: hoverStyle()
            ? `translateX(${ratingOptions.length * -TRANSLATE_OFFSET_PX}px)`
            : undefined,
        }}
        className={cn(
          'bg-background absolute right-0 shrink-0 border border-transparent text-sm transition-transform group-hover:bg-transparent',
          {
            'z-10': props.awardedPoints === null,
          },
        )}
      >
        N/A
      </Button>

      {ratingOptions?.map((option, index) => (
        <Button
          key={index}
          variant="ghost"
          size="icon"
          disabled={props.hasActiveDispute}
          onMouseOver={() => setHoverIndex(index)}
          onClick={() => handleChange(option.value)}
          style={{
            transform:
              (!props.finalized && props.awardedPoints === null && !dirty) || props.hover
                ? `translateX(${index * -TRANSLATE_OFFSET_PX}px)`
                : undefined,
          }}
          className={cn(
            'bg-background absolute right-0 shrink-0 border border-transparent pt-1 text-lg transition-transform group-hover:bg-transparent',
            {
              'z-10': props.awardedPoints === option.value,
            },
          )}
        >
          {option.value === 0 && props.autoFail ? '☠️' : option.label}
        </Button>
      ))}
    </div>
  )
}

const TRANSLATE_OFFSET_PX = 34
